import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Switch,
  Typography
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { usePayment } from '../contexts/PaymentContext';
import { THEME_COLORS } from '../constants/theme';
import StripeCheckout from './StripeCheckout';
import api from '../config/api';
import {
  billingIntervalFromYearly,
  isCurrentSelfServePlan,
  partnerCheckoutTierType,
  partnerPlanCtaLabel,
  partnerPlanUsageSummary,
  planNameKey,
  isScheduledSelfServePlan,
  formatOverLimitSummary
} from '../utils/partnerPlan';
import {
  PARTNER_PLANS,
  partnerPlanFeatures,
  partnerSalesMailto,
  PARTNER_ONBOARDING_TIER_TYPE,
  PARTNER_DEVELOPER_API_TIER_TYPE
} from '../config/plans';
import PartnerOnboardingCard, { PartnerDeveloperApiCard } from './PartnerAddonCard';

const PartnerPlanWall = ({
  open,
  onClose,
  onSuccess,
  onContactSales,
  reason = 'billing',
  entitlements: entitlementsProp = null
}) => {
  const { t } = useTranslation();
  const { createCheckoutSession, loading, error, clearError } = usePayment();
  const [yearly, setYearly] = useState(false);
  const [entitlements, setEntitlements] = useState(entitlementsProp);
  const [fetching, setFetching] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [checkoutData, setCheckoutData] = useState(null);
  const [selectedTier, setSelectedTier] = useState(null);
  const [cancelScheduleLoading, setCancelScheduleLoading] = useState(false);

  const loadEntitlements = useCallback(async () => {
    try {
      setFetching(true);
      const response = await api.get('/partners/plan');
      if (response?.data?.entitlements) {
        setEntitlements(response.data.entitlements);
      }
    } catch (err) {
      console.error('Error loading partner plan:', err);
    } finally {
      setFetching(false);
    }
  }, []);

  useEffect(() => {
    if (entitlementsProp) {
      setEntitlements(entitlementsProp);
    }
  }, [entitlementsProp]);

  useEffect(() => {
    if (open && !entitlementsProp) {
      loadEntitlements();
    }
  }, [open, entitlementsProp, loadEntitlements]);

  useEffect(() => {
    if (!entitlements?.billingInterval) return;
    setYearly(entitlements.billingInterval === 'year');
  }, [entitlements?.billingInterval]);

  const handleClose = () => {
    setCheckoutOpen(false);
    setCheckoutData(null);
    setSelectedTier(null);
    clearError();
    onClose?.();
  };

  const startCheckout = async (tierType, { skipBillingInterval = false } = {}) => {
    try {
      clearError();
      setSelectedTier(tierType);
      const billingInterval = skipBillingInterval ? undefined : billingIntervalFromYearly(yearly);
      const sessionData = await createCheckoutSession({
        tripId: null,
        tierType,
        ...(billingInterval ? { billingInterval, metadata: { billing_interval: billingInterval } } : {}),
        usePartnerAuth: true
      });

      if (sessionData?.success && !sessionData.clientSecret && !sessionData.url) {
        await loadEntitlements();
        onSuccess?.(sessionData);
        handleClose();
        return;
      }

      if (sessionData?.clientSecret) {
        setCheckoutData(sessionData);
        setCheckoutOpen(true);
      }
    } catch (err) {
      console.error('Error creating partner checkout:', err);
    }
  };

  const handleSelect = async (plan) => {
    if (!plan.selfServe) {
      window.location.assign(partnerSalesMailto());
      onContactSales?.(plan.id);
      return;
    }
    const tierType = partnerCheckoutTierType(plan);
    if (!tierType) return;
    await startCheckout(tierType);
  };

  const handleOnboardingPurchase = () => startCheckout(PARTNER_ONBOARDING_TIER_TYPE, { skipBillingInterval: true });
  const handleDeveloperApiPurchase = () => startCheckout(PARTNER_DEVELOPER_API_TIER_TYPE, { skipBillingInterval: true });

  const handlePaymentSuccess = async (data) => {
    setCheckoutOpen(false);
    setCheckoutData(null);
    await loadEntitlements();
    onSuccess?.(data);
    handleClose();
  };

  const handleCancelScheduledChange = async () => {
    try {
      setCancelScheduleLoading(true);
      const response = await api.delete('/partners/plan/scheduled-change');
      setEntitlements(response.data.entitlements || null);
      onSuccess?.({ cancelledSchedule: true });
    } catch (err) {
      console.error('Error cancelling scheduled plan change:', err);
    } finally {
      setCancelScheduleLoading(false);
    }
  };

  const currentTier = entitlements?.tier || 'free';
  const currentInterval = entitlements?.billingInterval;
  const selectedInterval = billingIntervalFromYearly(yearly);

  return (
    <>
      <Dialog open={open && !checkoutOpen} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {t('partnerPlans.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('partnerPlans.subtitle')}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} aria-label={t('common.close')}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ overflow: 'visible' }}>
          {reason !== 'billing' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t(
                reason === 'group'
                  ? 'partnerPlans.groupTripLocked'
                  : reason === 'marketplace'
                    ? 'partnerPlans.marketplaceLocked'
                    : reason === 'featured'
                      ? 'partnerPlans.featuredLocked'
                      : reason === 'clients'
                        ? 'partnerClientInvite.bespokeOnly'
                        : 'partnerPlans.limitReached'
              )}
            </Alert>
          )}

          {fetching && !entitlements ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {entitlements && (
                <Box sx={{ mb: 3 }}>
                  <Chip
                    label={`${t('partnerPlans.currentPlan')}: ${entitlements.planName}`}
                    color="primary"
                    sx={{ mb: 1, mr: 1 }}
                  />
                  {entitlements.scheduledPlan && (
                    <Chip
                      label={t('partnerPlans.scheduledChangeTitle')}
                      color="info"
                      sx={{ mb: 1, mr: 1 }}
                    />
                  )}
                  {entitlements.foundingPartner && (
                    <Chip label={t('partnerPlans.founding')} color="success" sx={{ mb: 1 }} />
                  )}
                  {entitlements.scheduledPlan && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {t('partnerPlans.scheduledChangeBody', {
                        currentPlan: entitlements.planName,
                        nextPlan: entitlements.scheduledPlan.planName,
                        date: entitlements.scheduledPlan.effectiveAt
                          ? new Date(String(entitlements.scheduledPlan.effectiveAt).replace(' ', 'T')).toLocaleDateString()
                          : t('partnerProfile.noRenewal')
                      })}
                    </Typography>
                  )}
                  {entitlements.scheduledPlan && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleCancelScheduledChange}
                      disabled={cancelScheduleLoading}
                      sx={{ mb: 1 }}
                    >
                      {cancelScheduleLoading
                        ? t('partnerPlans.cancellingScheduledChange')
                        : t('partnerPlans.cancelScheduledChange')}
                    </Button>
                  )}
                  {entitlements.scheduledOverLimit && entitlements.scheduledPlan && (
                    <Typography variant="body2" color="warning.main" sx={{ mb: 1 }}>
                      {t('partnerPlans.scheduledOverLimitWarning', {
                        date: entitlements.scheduledPlan.effectiveAt
                          ? new Date(String(entitlements.scheduledPlan.effectiveAt).replace(' ', 'T')).toLocaleDateString()
                          : t('partnerProfile.noRenewal'),
                        items: formatOverLimitSummary(entitlements.scheduledOverLimit, t)
                      })}
                    </Typography>
                  )}
                  {/* Trips are uncapped on every tier, so the only meter worth
                      showing is seats. */}
                  <Typography variant="body2" color="text.secondary">
                    {partnerPlanUsageSummary(entitlements, t)}
                  </Typography>
                </Box>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                <Typography variant="body2">{t('partnerPlans.monthly')}</Typography>
                <Switch checked={yearly} onChange={(e) => setYearly(e.target.checked)} />
                <Typography variant="body2">{t('partnerPlans.yearly')}</Typography>
                {yearly && (
                  <Chip size="small" label={t('partnerPlans.save')} sx={{ ml: 1 }} />
                )}
              </Box>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
              )}

              <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, overflow: 'visible', pt: 2, alignItems: 'stretch' }}>
                {PARTNER_PLANS.map((plan) => {
                  const price = yearly ? plan.yearly : plan.monthly;
                  const isCurrent = isCurrentSelfServePlan(plan, currentTier, currentInterval, selectedInterval);
                  const isScheduled = isScheduledSelfServePlan(plan, entitlements?.scheduledPlan, selectedInterval);
                  const nameKey = planNameKey(plan.id);
                  const isContactSales = !plan.selfServe;
                  const canSelect = Boolean(partnerCheckoutTierType(plan) || isContactSales);
                  const showRecommendedBadge = plan.recommended && !isCurrent;
                  return (
                    <Card
                      key={plan.id}
                      variant="outlined"
                      sx={{
                        position: 'relative',
                        borderColor: plan.recommended ? THEME_COLORS.primary.main : 'divider',
                        borderWidth: plan.recommended ? 2 : 1,
                        overflow: 'visible',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                    >
                      {showRecommendedBadge && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            px: 1.25,
                            py: 0.4,
                            bgcolor: THEME_COLORS.primary.main,
                            color: '#fff',
                            fontSize: 11,
                            fontWeight: 600,
                            lineHeight: 1.2,
                            letterSpacing: '0.04em',
                            borderRadius: '999px',
                            whiteSpace: 'nowrap',
                            zIndex: 1
                          }}
                        >
                          {t('partnerPlans.mostAgents')}
                        </Box>
                      )}
                      <CardContent
                        sx={{
                          pt: 2,
                          flex: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          height: '100%'
                        }}
                      >
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {t(nameKey)}
                        </Typography>
                        <Typography
                          variant="h4"
                          component={isContactSales ? 'a' : 'p'}
                          href={isContactSales ? partnerSalesMailto() : undefined}
                          sx={{
                            fontWeight: 800,
                            my: 1,
                            ...(isContactSales ? { color: 'inherit', textDecoration: 'none' } : {})
                          }}
                        >
                          {isContactSales
                            ? t('partnerPlans.callUs')
                            : price === 0
                              ? t('partnerPlans.free')
                              : `$${price}`}
                          {!isContactSales && price > 0 && (
                            <Typography component="span" variant="body2" color="text.secondary">
                              {yearly ? t('partnerPlans.perYear') : t('partnerPlans.perMonth')}
                            </Typography>
                          )}
                        </Typography>
                        <Box sx={{ flex: 1 }}>
                          {partnerPlanFeatures(plan, t).map((feature) => (
                            <Typography key={feature} variant="body2" sx={{ mb: 0.5 }}>
                              ✓ {feature}
                            </Typography>
                          ))}
                        </Box>
                        <Box sx={{ minHeight: 28, mt: 1, display: 'flex', alignItems: 'flex-end' }}>
                          {plan.trialDays > 0 && !isCurrent && !isContactSales ? (
                            <Typography variant="caption" color="text.secondary">
                              {t('partnerPlans.trialLength', { days: plan.trialDays })}
                            </Typography>
                          ) : null}
                        </Box>
                        <Button
                          fullWidth
                          variant={plan.recommended ? 'contained' : 'outlined'}
                          disabled={isCurrent || isScheduled || loading || !canSelect}
                          {...(isContactSales
                            ? { component: 'a', href: partnerSalesMailto() }
                            : { onClick: () => handleSelect(plan) })}
                          sx={{ mt: 1.5 }}
                        >
                          {partnerPlanCtaLabel({
                            plan,
                            currentTier,
                            currentInterval,
                            yearly,
                            t,
                            name: t(nameKey),
                            scheduledPlan: entitlements?.scheduledPlan
                          })}
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
                <PartnerDeveloperApiCard
                  onPurchase={handleDeveloperApiPurchase}
                  loading={loading}
                  included={Boolean(entitlements?.features?.hasProductionApi)}
                  canPurchase={Boolean(entitlements?.features?.hasApiSandbox) || entitlements?.tier === 'bespoke'}
                />
                <PartnerOnboardingCard onPurchase={handleOnboardingPurchase} loading={loading} />
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>

      {checkoutOpen && checkoutData?.clientSecret && (
        <StripeCheckout
          open={checkoutOpen}
          onClose={() => {
            setCheckoutOpen(false);
            setCheckoutData(null);
            setSelectedTier(null);
          }}
          clientSecret={checkoutData.clientSecret}
          tripTitle={selectedTier === PARTNER_ONBOARDING_TIER_TYPE
            ? t('partnerPlans.onboardingCard.checkoutTitle')
            : selectedTier === PARTNER_DEVELOPER_API_TIER_TYPE
              ? t('partnerPlans.developerApi.checkoutTitle')
              : t('partnerPlans.checkoutTitle')}
          upgradeType={selectedTier}
          onSuccess={handlePaymentSuccess}
        />
      )}
    </>
  );
};

export default PartnerPlanWall;
